"use client";

import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useLocale, useTranslations } from "next-intl";
import { useState, useEffect } from "react";

// Load Stripe just once to avoid recreating it on every render
let stripePromise: ReturnType<typeof loadStripe> | null = null;

const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY!);
  }
  return stripePromise;
};

interface CheckoutFormProps {
  clientSecret: string;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ clientSecret }) => {
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const stripe = getStripe();
  const locale = useLocale();
  const t = useTranslations("BankValuation.paymentStep.checkout");

  // Handle events from the embedded checkout
  const handleCheckoutEvents = (event: any) => {
    switch (event.type) {
      case "checkout.complete":
        setCheckoutComplete(true);
        break;
      case "checkout.error":
        setCheckoutError(t("paymentFailed"));
        break;
      default:
        break;
    }
  };

  // Set up event listener for embedded checkout events
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type && event.data.type.startsWith("checkout.")) {
        handleCheckoutEvents(event.data);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // If checkout is complete, show a success message or redirect
  if (checkoutComplete) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-green-200 bg-green-50 p-6">
        <h3 className="mb-2 text-xl font-medium text-green-700">{t("successTitle")}</h3>
        <p className="mb-4 text-green-600">{t("successMessage")}</p>
        <div className="animate-pulse text-center text-sm text-green-500">{t("redirecting")}</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {checkoutError && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{checkoutError}</div>
      )}

      <div className="overflow-hidden rounded-lg">
        <EmbeddedCheckoutProvider
          stripe={stripe}
          options={{
            clientSecret,
            onComplete: () => setCheckoutComplete(true),
          }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>

      <div className="mt-4 text-center text-xs text-gray-500">{t("securedByStripe")}</div>
    </div>
  );
};

export default CheckoutForm;
